/**
 * Export layout templates for ride video export.
 * Each template defines which overlays (graph, GPS map) are included and their layout.
 * Custom templates store layout: { graphHeightPct?, gps?: { xPct, yPct, widthPct, heightPct } } (0–1).
 */

export const EXPORT_TEMPLATE_IDS = {
  VIDEO_GRAPH_GPS: 'video_graph_gps',
  VIDEO_GRAPH_ONLY: 'video_graph_only',
  VIDEO_GPS_ONLY: 'video_gps_only',
};

export const CUSTOM_TEMPLATE_PREFIX = 'custom_';
const STORAGE_KEY = 'victorise_export_custom_templates';

export const exportTemplates = [
  {
    id: EXPORT_TEMPLATE_IDS.VIDEO_GRAPH_GPS,
    label: 'Video + Graph + GPS',
    description: 'Video with telemetry graph at bottom and GPS map in upper right',
    requiresTelemetry: true,
    requiresGps: true,
  },
  {
    id: EXPORT_TEMPLATE_IDS.VIDEO_GRAPH_ONLY,
    label: 'Video + Graph only',
    description: 'Video with telemetry graph at bottom',
    requiresTelemetry: true,
    requiresGps: false,
  },
  {
    id: EXPORT_TEMPLATE_IDS.VIDEO_GPS_ONLY,
    label: 'Video + GPS only',
    description: 'Video with GPS map in upper right',
    requiresTelemetry: false,
    requiresGps: true,
  },
];

/**
 * @typedef {{ graphHeightPct?: number, gps?: { xPct: number, yPct: number, widthPct: number, heightPct: number } }} ExportLayoutConfig
 */

/**
 * @returns {Array<{ id: string, label: string, layout: ExportLayoutConfig }>}
 */
export function getCustomTemplates() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

/**
 * @param {{ id?: string, label: string, layout: ExportLayoutConfig }} template
 * @returns {string} id of saved template
 */
export function saveCustomTemplate(template) {
  const list = getCustomTemplates();
  const id = template.id || CUSTOM_TEMPLATE_PREFIX + Date.now();
  const entry = { id, label: template.label || 'Custom', layout: template.layout };
  const idx = list.findIndex((t) => t.id === id);
  if (idx >= 0) list[idx] = entry;
  else list.push(entry);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  return id;
}

/**
 * @param {string} id
 */
export function deleteCustomTemplate(id) {
  const list = getCustomTemplates().filter((t) => t.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

/**
 * Get templates available for a run based on its data (built-in + saved custom).
 * @param {{ hasTelemetry: boolean, hasGps: boolean }} runCapabilities
 * @returns {Array<{ id: string, label: string, description?: string, disabled?: boolean, reason?: string, custom?: boolean, layout?: ExportLayoutConfig }>}
 */
export function getAvailableTemplates(runCapabilities) {
  const { hasTelemetry = false, hasGps = false } = runCapabilities || {};
  const builtIn = exportTemplates.map((t) => {
    const needsTelemetry = t.requiresTelemetry && !hasTelemetry;
    const needsGps = t.requiresGps && !hasGps;
    const disabled = needsTelemetry || needsGps;
    let reason = null;
    if (needsTelemetry && needsGps) reason = 'This run has no telemetry or GPS data';
    else if (needsTelemetry) reason = 'This run has no telemetry data';
    else if (needsGps) reason = 'This run has no GPS data';
    return { ...t, disabled, reason };
  });
  const custom = getCustomTemplates().map((t) => {
    const hasGraph = (t.layout?.graphHeightPct ?? 0) > 0;
    const hasGpsBox = !!t.layout?.gps;
    const needsTelemetry = hasGraph && !hasTelemetry;
    const needsGps = hasGpsBox && !hasGps;
    const disabled = needsTelemetry || needsGps;
    let reason = null;
    if (needsTelemetry && needsGps) reason = 'This run has no telemetry or GPS data';
    else if (needsTelemetry) reason = 'This run has no telemetry data';
    else if (needsGps) reason = 'This run has no GPS data';
    return {
      id: t.id,
      label: t.label,
      description: 'Custom layout',
      disabled,
      reason,
      custom: true,
      layout: t.layout,
    };
  });
  return [...builtIn, ...custom];
}
